/**
 * ModelRequestResponse Component - Reactive Version
 *
 * Uses the reactive GraphStore pattern:
 * - useNode(modelRequest.model_response) for reactive access to response
 * - useNodes(modelResponse.parts) for reactive access to parts
 * - No manual DAG reconstruction needed
 */

import React from 'react';
import { Brain, Loader2, AlertTriangle } from 'lucide-react';
import { TextPartRenderer } from '@sourcepilot/ui/shell/chat/TextPartRenderer';
import { ToolCallPartRenderer } from '@sourcepilot/ui/shell/chat/ToolCallPartRenderer';
import { ThinkingPartRenderer } from '@sourcepilot/ui/shell/chat/ThinkingPartRenderer';
import { TextPartReadAPISchema as TextPart } from '@sourcepilot/generated/chat/types/textpart';
import { ToolCallPartReadAPISchema as ToolCallPart } from '@sourcepilot/generated/chat/types/toolcallpart';
import { ThinkingPartReadAPISchema as ThinkingPart } from '@sourcepilot/generated/chat/types/thinkingpart';
import { ModelRequest } from '@sourcepilot/generated/chat/api/modelrequest';
import { graphStore } from '@sourcepilot/framework/graph-store';
import { useNodes, useNode } from '@sourcepilot/framework/relationship-manager';

import { z } from 'zod';

interface ModelRequestResponseProps {
  modelRequest: ModelRequest;
  renderConfig: {
    showAllToolCalls?: boolean;
    showSysPrompts?: boolean;
    showModelRequests?: boolean;
  };
}

export function ModelRequestResponse({
  modelRequest,
  renderConfig,
}: ModelRequestResponseProps) {
  // REACTIVE: Get model response from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  const modelResponse = useNode(modelRequest.model_response);

  if (!modelResponse) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {/* Optional: Show ModelRequest info if configured */}
      {renderConfig.showModelRequests && (
        <div className="text-xs text-gray-500 mb-1">
          Model Request: {modelRequest?.model_name || 'Unknown'}
        </div>
      )}

      <ModelResponseParts
        modelResponse={modelResponse}
        renderConfig={renderConfig}
      />
    </div>
  );
}

/**
 * Helper component to reactively access parts for a model response
 */
function ModelResponseParts({
  modelResponse,
  renderConfig
}: {
  modelResponse: any;
  renderConfig: {
    showAllToolCalls?: boolean;
    showSysPrompts?: boolean;
    showModelRequests?: boolean;
  };
}) {
  // REACTIVE: Get response parts from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  // Apply client-side ordering by order_index (memoized internally)
  const sortedParts = useNodes(modelResponse.parts).orderBy('order_index').value();

  // While the model response hasn't finished, surface *something* so the
  // user knows the agent is working — even if no parts have streamed in
  // yet (common during the initial thinking phase before Gemini emits
  // the first ThinkingPart), or if thinking tokens are disabled/masked.
  // `is_complete` on ModelResponse flips true once the response is final.
  const isResponseComplete = modelResponse?.is_complete === true;
  const hasStreamingPart = sortedParts.some((p: any) => p?.is_streaming === true);
  const latestStreamingPart = [...sortedParts].reverse().find((p: any) => p?.is_streaming === true) as any;
  const latestStreamingIsThinking =
    latestStreamingPart && (latestStreamingPart?._label || latestStreamingPart?.type) === 'ThinkingPart';

  return (
    <div className="flex flex-col">
      {sortedParts.map((part: any) => {
        // Use _label instead of type (our ORM convention)
        const partType = (part as any)?._label || (part as any)?.type;

        if (partType === 'TextPart') {
          return (
            <TextPartRenderer
              key={part.id}
              part={part as z.infer<typeof TextPart>}
              isStreaming={part?.is_streaming || false}
            />
          );
        } else if (partType === 'ToolCallPart') {
          // Check if this tool call should be shown in the UI
          // Default to true if show_in_ui is not specified (for backwards compatibility)
          if (part.show_in_ui === false) {
            return null;
          }

          return (
            <ToolCallPartRenderer
              key={part.id}
              part={part as z.infer<typeof ToolCallPart>}
              renderConfig={renderConfig}
              isStreaming={part?.is_streaming || false}
            />
          );
        } else if (partType === 'ThinkingPart') {
          return (
            <ThinkingPartRenderer
              key={part.id}
              part={part as z.infer<typeof ThinkingPart>}
              isStreaming={part?.is_streaming || false}
            />
          );
        }
        return null;
      })}

      {/* Waiting / thinking indicator — visible only while the response is
          genuinely in-flight (is_streaming). A response that is neither
          complete nor streaming ended terminally (model error / timeout /
          cancel); show a quiet retryable notice rather than an endless
          "Waiting for model…" spinner. The error path leaves is_complete=false
          but flips is_streaming=false, so gating on is_streaming is what stops
          a dead turn spinning forever. */}
      {!isResponseComplete &&
        (modelResponse?.is_streaming === true ? (
          <div className="flex items-center gap-2 p-2 text-sm text-gray-500 italic">
            {latestStreamingIsThinking ? (
              <>
                <Brain className="w-4 h-4 animate-pulse" />
                <span>Thinking…</span>
              </>
            ) : hasStreamingPart ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Responding…</span>
              </>
            ) : (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Waiting for model…</span>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 text-sm text-amber-600 italic">
            <AlertTriangle className="w-4 h-4" />
            <span>The model stopped before finishing — you can retry.</span>
          </div>
        ))}
    </div>
  );
}
